import { useMemo, useState } from "react";
import { Box, Container, Grid, Slide } from "@mui/material";
import {
  Card,
  ConditionalRenderer,
  DataDisplayMessage,
  Filters,
  Header,
  LatestReport,
  TabsFilters,
  useLanguageTranslator,
} from "../../../components";
import {
  PolicyStatus,
  usePoliciesDocumentsCall,
  usePublicPoliciesCall,
} from "../../../hooks";

function PublicPolicies() {
  const [inputValue, setInputValue] = useState("");
  const [filters, setFilters] = useState({
    country: "",
    startYear: "",
    endYear: "",
    theme: "",
    geographicalArea: "",
  });
  const [currTab, setCurrTab] = useState<PolicyStatus | "all">("all");

  const { translator } = useLanguageTranslator();

  const { policyDocuments, loading: policyDocumentsLoading } =
    usePoliciesDocumentsCall({
      variables: {
        limit: 8,
        ...(filters?.country && filters?.country !== "0"
          ? { countryId: Number(filters?.country) }
          : {}),
        ...(filters?.theme && filters?.theme !== "0"
          ? { themeId: Number(filters?.theme) }
          : {}),
        ...(filters?.startYear
          ? { startYear: Number(filters?.startYear) }
          : {}),
        ...(filters?.endYear ? { endYear: Number(filters?.endYear) } : {}),
        ...(filters?.geographicalArea && filters?.geographicalArea !== "0"
          ? { geographicalAreaId: Number(filters?.geographicalArea) }
          : {}),
      },
    });

  const {
    policies = [],
    loading,
    refetch,
    tabs,
  } = usePublicPoliciesCall({
    variables: {
      limit: 8,
      ...(filters?.country && filters?.country !== "0"
        ? { countryId: Number(filters?.country) }
        : {}),
      ...(filters?.theme && filters?.theme !== "0"
        ? { themeId: Number(filters?.theme) }
        : {}),
      ...(filters?.startYear ? { startYear: Number(filters?.startYear) } : {}),
      ...(filters?.endYear ? { endYear: Number(filters?.endYear) } : {}),
      ...(filters?.geographicalArea && filters?.geographicalArea !== "0"
        ? { geographicalAreaId: Number(filters?.geographicalArea) }
        : {}),
    },
    currTab: currTab,
  });

  const filteredPoliciesList = useMemo(() => {
    if (!inputValue) return policies || [];

    const searchValue = inputValue
      ?.normalize("NFD")
      ?.replace(/[\u0300-\u036f]/g, "")
      ?.toLowerCase();

    return policies?.filter((item: any) =>
      item?.title
        ?.normalize("NFD")
        ?.replace(/[\u0300-\u036f]/g, "")
        ?.toLowerCase()
        ?.includes(searchValue)
    );
  }, [inputValue, policies]);

  const filteredPolicyDocumentsList = useMemo(() => {
    if (!inputValue) return policyDocuments || [];

    const searchValue = inputValue
      ?.normalize("NFD")
      ?.replace(/[\u0300-\u036f]/g, "")
      ?.toLowerCase();

    return policyDocuments?.filter((item: any) =>
      item?.filename
        ?.normalize("NFD")
        ?.replace(/[\u0300-\u036f]/g, "")
        ?.toLowerCase()
        ?.includes(searchValue)
    );
  }, [inputValue, policyDocuments]);

  return (
    <Box pb={4}>
      <Header
        title={translator("Policies")}
        subtitle=""
        setInputValue={setInputValue}
        inputValue={inputValue}
      />
      <Filters
        setFilters={setFilters}
        filters={filters}
        isPublicPolicies={true}
      />
      <Container sx={{ my: 3 }}>
        <TabsFilters
          currTab={currTab}
          setCurrTab={setCurrTab}
          listItems={tabs}
          heading={`${translator("Latest Policies")}`}
        />
        <Grid container spacing={2}>
          <ConditionalRenderer condition={policies?.length > 0}>
            {filteredPoliciesList?.map((p: any, i: any) => (
              <Grid key={`${i}`} item xs={12} md={6} lg={3}>
                <Slide in timeout={180 * (i + 1)} direction="right">
                  <Card {...p} path={`${p?.id}`} sx={{ height: "100%" }} />
                </Slide>
              </Grid>
            ))}
          </ConditionalRenderer>
          <DataDisplayMessage data={policies} loading={loading} />
        </Grid>
      </Container>

      <LatestReport
        items={filteredPolicyDocumentsList}
        loading={policyDocumentsLoading}
        path="/policies/view-all-Reports"
      />
    </Box>
  );
}

export { PublicPolicies };
